/**
 * KeyboardShortcuts.tsx — An overlay that shows all keyboard shortcuts.
 * Opens with '?' key and closes with Escape or clicking outside.
 * Mount once in Layout.tsx.
 */
import React, { useEffect, useState } from 'react';

const SHORTCUTS: { section: string; items: { keys: string[]; label: string }[] }[] = [
    {
        section: 'Navigation',
        items: [
            { keys: ['G', 'H'], label: 'Go to Dashboard' },
            { keys: ['G', 'P'], label: 'Go to Projects' },
            { keys: ['G', 'E'], label: 'Go to Records / Entities' },
            { keys: ['G', 'M'], label: 'Go to Profile' },
        ],
    },
    {
        section: 'Search',
        items: [
            { keys: ['⌘', 'K'], label: 'Open command palette' },
            { keys: ['/'], label: 'Focus search' },
        ],
    },
    {
        section: 'Tasks',
        items: [
            { keys: ['N'], label: 'New task (on project page)' },
            { keys: ['Enter'], label: 'Open selected task' },
            { keys: ['E'], label: 'Quick edit inline' },
            { keys: ['Delete'], label: 'Delete selected task' },
        ],
    },
    {
        section: 'General',
        items: [
            { keys: ['?'], label: 'Show keyboard shortcuts' },
            { keys: ['Esc'], label: 'Close modal / overlay' },
            { keys: ['⌘', 'S'], label: 'Save (in forms)' },
        ],
    },
];

function Kbd({ k }: { k: string }) {
    return (
        <kbd style={{
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            minWidth: 24, height: 24, padding: '0 6px',
            background: 'var(--color-surface)', border: '1px solid var(--color-border)',
            borderBottom: '2px solid var(--color-border)',
            borderRadius: 4, fontFamily: 'monospace',
            fontSize: 11, fontWeight: 700, color: 'var(--color-text)',
        }}>{k}</kbd>
    );
}

export function KeyboardShortcutsOverlay() {
    const [open, setOpen] = useState(false);

    useEffect(() => {
        function onKey(e: KeyboardEvent) {
            const tag = (e.target as HTMLElement).tagName;
            if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
            if (e.key === '?') setOpen(o => !o);
            if (e.key === 'Escape') setOpen(false);
        }
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, []);

    if (!open) return null;

    return (
        <div
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 900, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            onClick={() => setOpen(false)}
        >
            <div
                onClick={e => e.stopPropagation()}
                style={{
                    background: 'var(--color-surface)', borderRadius: 'var(--radius-lg)', padding: 'var(--space-6)',
                    maxWidth: 560, width: '90%', maxHeight: '80vh', overflowY: 'auto',
                    boxShadow: 'var(--shadow-xl)',
                }}
            >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 'var(--space-4)' }}>
                    <h2 style={{ margin: 0, fontSize: 'var(--font-size-lg)', fontWeight: 700 }}>⌨️ Keyboard Shortcuts</h2>
                    <button className="btn btn-ghost btn-sm" onClick={() => setOpen(false)}>✕</button>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-5)' }}>
                    {SHORTCUTS.map(section => (
                        <div key={section.section}>
                            <div style={{ fontSize: 'var(--font-size-xs)', fontWeight: 700, color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 'var(--space-2)' }}>
                                {section.section}
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
                                {section.items.map(item => (
                                    <div key={item.label} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--space-2)' }}>
                                        <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-muted)' }}>{item.label}</span>
                                        <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                                            {item.keys.map((k, i) => (
                                                <React.Fragment key={k}>
                                                    {i > 0 && item.keys.length > 1 && i < item.keys.length && (
                                                        <span style={{ fontSize: 10, color: 'var(--color-text-faint)' }}>+</span>
                                                    )}
                                                    <Kbd k={k} />
                                                </React.Fragment>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
                <p style={{ marginTop: 'var(--space-4)', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-faint)', textAlign: 'center' }}>
                    Press <Kbd k="?" /> to toggle this overlay
                </p>
            </div>
        </div>
    );
}
